import { useState, useContext } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { FeedbackUIContext } from '#context/feedback-ui-context';
import { formatDateTime } from '#common/utils';
import {
  useListApiKeysQuery,
  useCreateApiKeyMutation,
  useRevokeApiKeyMutation,
  useGetApiAccessLogQuery,
  useGetEligibleKeyUsersQuery,
} from '#services/api-access';

interface ApiKey {
  _id: string;
  keyPrefix?: string;
  label?: string;
  userDisplayName?: string;
  userName?: string;
  userRole?: string;
  createdAt?: string;
  lastUsedAt?: string;
  revokedAt?: string;
  [key: string]: unknown;
}

interface CreatedKey {
  key: string;
  label?: string;
  keyPrefix?: string;
  [key: string]: unknown;
}

interface AccessLogEntry {
  timestamp?: string;
  keyPrefix?: string;
  endpoint?: string;
  method?: string;
  success?: boolean;
  responseStatus?: number | string;
  durationMs?: number;
  [key: string]: unknown;
}

interface EligibleUser {
  id: string | number;
  name?: string;
  username?: string;
  role?: string;
  [key: string]: unknown;
}

interface RevokeDialogState {
  open: boolean;
  keyId: string | number | null;
  keyPrefix: string;
}

// shared formatter so output doesn't vary with the viewer's browser locale
function formatDate(dateStr?: string) {
  if (!dateStr) return '—';
  return formatDateTime(dateStr, '—');
}

function UserCell({ userDisplayName, userName, userRole }: {
  userDisplayName?: string | undefined;
  userName?: string | undefined;
  userRole?: string | undefined;
}) {
  if (!userName) return '—';
  return `${userDisplayName || userName} (${userName}) — ${userRole}`;
}

export default function ApiKeyList() {
  const { showSnackbar } = useContext(FeedbackUIContext)!;

  // Keys + log
  const { data: keysData, isLoading: keysLoading } = useListApiKeysQuery(undefined);
  const [revokeApiKey] = useRevokeApiKeyMutation();
  const [revokeDialog, setRevokeDialog] = useState<RevokeDialogState>({ open: false, keyId: null, keyPrefix: '' });
  const [logFilterKeyId, setLogFilterKeyId] = useState('');

  const { data: logData, isLoading: logLoading } = useGetApiAccessLogQuery(
    logFilterKeyId ? { apiKeyId: logFilterKeyId } : {}
  );

  // Create form
  const { data: usersData, isLoading: usersLoading, isError: usersError } = useGetEligibleKeyUsersQuery(undefined);
  const [createApiKey, { isLoading: creating }] = useCreateApiKeyMutation();
  const [label, setLabel] = useState('');
  const [userId, setUserId] = useState('');
  const [createdKey, setCreatedKey] = useState<CreatedKey | null>(null);

  const keys = ((keysData?.data || []) as ApiKey[]).filter((k) => !k.revokedAt);
  const logs = ((logData as { data?: AccessLogEntry[] })?.data || []);
  const eligibleUsers = ((usersData as { data?: EligibleUser[] })?.data || []);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    try {
      const result = await createApiKey({ label, userId: Number(userId) }).unwrap();
      setCreatedKey(result.data as CreatedKey);
      setLabel('');
      setUserId('');
    } catch (err) {
      showSnackbar((err as { data?: { message?: string } })?.data?.message || 'Failed to create API key', 'error');
    }
  }

  function handleCopy() {
    void navigator.clipboard.writeText(createdKey!.key);
    showSnackbar('Key copied to clipboard', 'success');
  }

  function handleRevokeClick(keyId: string | number, keyPrefix?: string) {
    setRevokeDialog({ open: true, keyId, keyPrefix: keyPrefix || '' });
  }

  function handleRevokeCancel() {
    setRevokeDialog({ open: false, keyId: null, keyPrefix: '' });
  }

  async function handleRevokeConfirm() {
    const { keyId } = revokeDialog;
    setRevokeDialog({ open: false, keyId: null, keyPrefix: '' });
    try {
      await revokeApiKey({ keyId: keyId!.toString() }).unwrap();
      showSnackbar('Key revoked successfully', 'success');
    } catch (err) {
      showSnackbar((err as { data?: { message?: string } })?.data?.message || 'Failed to revoke key', 'error');
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="h5">API Keys</Typography>
        <Chip label="Experimental AI" size="small" color="warning" variant="outlined" />
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: 3,
          maxWidth: 680
        }}>
        API keys allow external agents (e.g. automation tools, integrations) to access SWM on behalf
        of a staff or admin account without sharing login credentials. Keys are long-lived and should
        be treated as passwords — share them only with trusted systems. A key is shown only once at
        creation time; if lost, revoke it and generate a new one. Only admin and staff accounts are
        eligible to have API keys.
        <Typography component="span" variant="body2" color="success"> Only Admins can create API keys.</Typography>
      </Typography>
      <Typography variant="h6" sx={{ mb: 2 }}>Generate New Key</Typography>
      {usersError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load eligible users. Cannot create key.
        </Alert>
      )}
      {createdKey && (
        <Alert
          severity="success"
          onClose={() => setCreatedKey(null)}
          sx={{ mb: 2, maxWidth: 640 }}
        >
          <Typography variant="body2" sx={{ mb: 1 }}>
            Key created — <strong>copy it now</strong>. It will not be shown again.
          </Typography>
          <Paper
            variant="outlined"
            sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.default' }}
          >
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>
              {createdKey.key}
            </Typography>
            <IconButton size="small" onClick={handleCopy} title="Copy to clipboard">
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Paper>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              mt: 0.5,
              display: 'block'
            }}>
            Label: <strong>{createdKey.label}</strong> &nbsp;|&nbsp; Prefix: <code>{createdKey.keyPrefix}</code>
          </Typography>
        </Alert>
      )}
      <Box component="form" onSubmit={(e) => { void handleCreate(e); }} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap', mb: 4 }}>
        <TextField
          label="Label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          required
          size="small"
          sx={{ width: 240 }}
          placeholder="e.g. Claude Email Agent"
        />
        <FormControl size="small" sx={{ width: 280 }} required>
          <InputLabel>User</InputLabel>
          <Select
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            label="User"
            disabled={usersLoading || usersError}
          >
            {usersLoading && (
              <MenuItem disabled><CircularProgress size={16} /></MenuItem>
            )}
            {eligibleUsers.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {user.name} ({user.username}) — {user.role}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          type="submit"
          variant="contained"
          disabled={creating || usersError || !label || !userId}
          sx={{ height: 40 }}
        >
          {creating ? <CircularProgress size={20} /> : 'Generate Key'}
        </Button>
      </Box>
      <Divider sx={{ mb: 3 }} />
      {keysLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Key Prefix</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Last Used</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {keys.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">No API keys found.</TableCell>
                </TableRow>
              )}
              {keys.map((key) => (
                <TableRow key={key._id}>
                  <TableCell><code>{key.keyPrefix}</code></TableCell>
                  <TableCell>{key.label}</TableCell>
                  <TableCell>
                    <UserCell
                      userDisplayName={key.userDisplayName}
                      userName={key.userName}
                      userRole={key.userRole}
                    />
                  </TableCell>
                  <TableCell>{formatDate(key.createdAt)}</TableCell>
                  <TableCell>{formatDate(key.lastUsedAt)}</TableCell>
                  <TableCell>
                    {key.revokedAt ? (
                      <Chip label="Revoked" color="error" size="small" />
                    ) : (
                      <Chip label="Active" color="success" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    {!key.revokedAt && (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleRevokeClick(key._id, key.keyPrefix)}
                      >
                        Revoke
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Typography variant="h6" sx={{ mb: 1 }}>Recent Activity</Typography>
      <Box sx={{ mb: 1 }}>
        <Select
          size="small"
          value={logFilterKeyId}
          onChange={(e) => setLogFilterKeyId(e.target.value)}
          displayEmpty
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All keys</MenuItem>
          {keys.map((key) => (
            <MenuItem key={key._id} value={key._id.toString()}>
              {key.keyPrefix} — {key.label}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Timestamp</TableCell>
              <TableCell>Key Prefix</TableCell>
              <TableCell>Endpoint</TableCell>
              <TableCell>Method</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Duration</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logLoading && (
              <TableRow>
                <TableCell colSpan={6} align="center"><CircularProgress size={20} /></TableCell>
              </TableRow>
            )}
            {!logLoading && logs.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">No activity found.</TableCell>
              </TableRow>
            )}
            {logs.map((entry, i) => (
              <TableRow key={i}>
                <TableCell>{formatDate(entry.timestamp)}</TableCell>
                <TableCell><code>{entry.keyPrefix}</code></TableCell>
                <TableCell>{entry.endpoint}</TableCell>
                <TableCell>{entry.method}</TableCell>
                <TableCell>
                  {entry.success ? (
                    <Chip label={entry.responseStatus} color="success" size="small" />
                  ) : (
                    <Chip label={entry.responseStatus} color="error" size="small" />
                  )}
                </TableCell>
                <TableCell>{entry.durationMs}ms</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={revokeDialog.open} onClose={handleRevokeCancel}>
        <DialogTitle>Revoke API Key</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Revoke key <strong>{revokeDialog.keyPrefix}</strong>? This cannot be undone — any agent using this key will immediately lose access.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRevokeCancel}>Cancel</Button>
          <Button onClick={() => { void handleRevokeConfirm(); }} color="error" variant="contained">
            Revoke
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
